import React, { useEffect, useState } from 'react';
import '../styles/ProgressDialog.css';

interface ProgressDialogProps {
  current: number;
  total: number;
  onCancel: () => void;
  isProcessing: boolean;
}

const ProgressDialog: React.FC<ProgressDialogProps> = ({
  current,
  total,
  onCancel,
  isProcessing,
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [estimatedRemaining, setEstimatedRemaining] = useState<number | null>(null);
  const [startTime] = useState(Date.now());
  const [processingRate, setProcessingRate] = useState<number | null>(null);

  useEffect(() => {
    if (!isProcessing) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      setElapsedTime(elapsed);

      if (current > 0) {
        const elapsedMs = now - startTime;
        const rate = current / (elapsedMs / 1000); // vehicles per second
        setProcessingRate(rate);

        const remaining = total - current;
        if (rate > 0 && remaining > 0) {
          const estimated = Math.ceil(remaining / rate);
          setEstimatedRemaining(estimated);
        }
      }
    }, 50); // Update every 50ms for very smooth updates

    return () => clearInterval(interval);
  }, [current, total, isProcessing, startTime]);

  const percentage = total > 0 ? Math.min(100, (current / total) * 100) : 0;

  const formatTime = (seconds: number): string => {
    // If less than a minute, show seconds
    if (seconds < 60) {
      return `${seconds}${seconds === 1 ? ' δευτερόλεπτο' : ' δευτερόλεπτα'}`;
    }
    
    const totalMinutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    // If less than an hour, show minutes (rounded, no seconds)
    if (totalMinutes < 60) {
      // Round up if there are 30+ seconds
      const roundedMinutes = remainingSeconds >= 30 ? totalMinutes + 1 : totalMinutes;
      return `${roundedMinutes}${roundedMinutes === 1 ? ' λεπτό' : ' λεπτά'}`;
    }
    
    // If an hour or more, show hours (rounded, no minutes/seconds)
    const hours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    // Round up if there are 30+ minutes
    const roundedHours = remainingMinutes >= 30 ? hours + 1 : hours;
    return `${roundedHours}${roundedHours === 1 ? ' ώρα' : ' ώρες'}`;
  };

  return (
    <div className="progress-dialog-overlay">
      <div className="progress-dialog">
        <div className="progress-header">
          <div className="progress-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <h2>Επεξεργασία σε Εξέλιξη</h2>
          <p className="progress-subtitle">Παρακαλώ περιμένετε...</p>
        </div>

        <div className="progress-content">
          <div className="progress-bar-container">
            <div className="progress-bar-background">
              <div 
                className="progress-bar-fill"
                style={{ width: `${percentage}%` }}
              >
                <div className="progress-bar-shine"></div>
              </div>
            </div>
            <div className="progress-percentage">
              {percentage.toFixed(1)}%
            </div>
          </div>

          <div className="progress-stats">
            <div className="stat-item">
              <div className="stat-label">Επεξεργασμένα:</div>
              <div className="stat-value">{current} / {total}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Απομένουν:</div>
              <div className="stat-value">{total - current}</div>
            </div>
          </div>

          <div className="progress-info">
            <div className="info-row">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>Χρόνος που πέρασε: <strong>{formatTime(elapsedTime)}</strong></span>
            </div>
            <div className="info-row">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              <span>
                {estimatedRemaining !== null && estimatedRemaining > 0 ? (
                  <>Εκτιμώμενος υπόλοιπος χρόνος: <strong>{formatTime(estimatedRemaining)}</strong></>
                ) : (
                  <span style={{ opacity: 0.5 }}>Εκτιμώμενος υπόλοιπος χρόνος: <strong>—</strong></span>
                )}
              </span>
            </div>
            <div className="info-row">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                <polyline points="17 6 23 6 23 12"/>
              </svg>
              <span>
                {processingRate !== null && processingRate > 0 ? (
                  <>Χρόνος επεξεργασίας: <strong>{(1 / processingRate).toFixed(1)}</strong> δευτ./όχημα</>
                ) : (
                  <span style={{ opacity: 0.5 }}>Χρόνος επεξεργασίας: <strong>—</strong></span>
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="progress-actions">
          <button 
            className="cancel-btn"
            onClick={onCancel}
            disabled={!isProcessing}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            Ακύρωση
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgressDialog;

